import { useState, useRef, type FormEvent } from 'react';
import { IMAGE_RULES, type Bookmark, type IconType } from '@td/shared';
import {
  useCreateBookmark,
  useUpdateBookmark,
  useUploadBookmarkImage,
  useDeleteBookmarkImage,
} from '../hooks/useBookmarks';
import { resizeToWebp } from '../lib/resizeImage';
import { ApiRequestError } from '../lib/api';
import { Modal } from './Modal';
import { EmojiPicker } from './EmojiPicker';

interface Props {
  bookmark: Bookmark | null; // null = creating
  onClose: () => void;
}

export function BookmarkEditor({ bookmark, onClose }: Props) {
  const create = useCreateBookmark();
  const update = useUpdateBookmark();
  const uploadImage = useUploadBookmarkImage();
  const removeImage = useDeleteBookmarkImage();
  const fileRef = useRef<HTMLInputElement>(null);

  const [label, setLabel] = useState(bookmark?.label ?? '');
  const [url, setUrl] = useState(bookmark?.url ?? '');
  const [category, setCategory] = useState(bookmark?.category ?? '');
  const [emoji, setEmoji] = useState(bookmark?.emoji ?? '');
  const [iconType, setIconType] = useState<IconType>(bookmark?.iconType ?? 'text');
  const [isQuickLink, setIsQuickLink] = useState(bookmark?.isQuickLink ?? false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);

    const payload = {
      label: label.trim(),
      url: url.trim(),
      category: category.trim() || null,
      emoji: iconType === 'emoji' ? emoji.trim() || null : null,
      iconType,
      isQuickLink,
    };

    try {
      const saved = bookmark
        ? await update.mutateAsync({ id: bookmark.id, ...payload })
        : await create.mutateAsync(payload);

      const file = fileRef.current?.files?.[0];
      if (file) {
        const blob = await resizeToWebp(file, IMAGE_RULES.bookmarkIconPx);
        await uploadImage.mutateAsync({ id: saved.id, file: blob });
      }
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiRequestError || err instanceof Error
          ? err.message
          : 'Could not save that.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      onClose={onClose}
      label="Bookmark"
      className="w-full max-w-md rounded-card bg-white p-5 shadow-lifted"
    >
      <h2 className="mb-4 text-lg">{bookmark ? 'Edit bookmark' : 'New bookmark'}</h2>

      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className="label" htmlFor="bm-label">
            Name
          </label>
          <input
            id="bm-label"
            className="field"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="PowerSchool"
            maxLength={60}
            required
            autoFocus
          />
        </div>

        <div>
          <label className="label" htmlFor="bm-url">
            Link
          </label>
          <input
            id="bm-url"
            className="field"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="powerschool.com"
            required
          />
        </div>

        <div>
          <label className="label" htmlFor="bm-cat">
            Category <span className="text-stone">(optional)</span>
          </label>
          <input
            id="bm-cat"
            className="field"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Curriculum"
            maxLength={40}
          />
        </div>

        <fieldset>
          <legend className="label">Tile face</legend>
          <div className="flex gap-2">
            {(['text', 'emoji', 'image'] as IconType[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setIconType(t)}
                className={[
                  'rounded-lg border px-3 py-1.5 text-sm capitalize transition-colors',
                  iconType === t
                    ? 'border-terracotta bg-terracotta text-cream'
                    : 'border-sand text-walnut hover:bg-linen',
                ].join(' ')}
              >
                {t}
              </button>
            ))}
          </div>
        </fieldset>

        {iconType === 'emoji' && <EmojiPicker value={emoji} onChange={setEmoji} />}

        {iconType === 'image' && (
          <div>
            <input
              ref={fileRef}
              type="file"
              accept={IMAGE_RULES.acceptedMimeTypes.join(',')}
              className="w-full text-sm text-walnut file:mr-3 file:rounded-lg file:border-0 file:bg-linen file:px-3 file:py-1.5 file:text-sm file:text-walnut"
              aria-label="Upload an icon"
            />
            <p className="mt-1 text-xs text-stone">
              JPEG, PNG, or WebP. Resized to {IMAGE_RULES.bookmarkIconPx}px automatically.
            </p>
            {bookmark?.hasImage && (
              <button
                type="button"
                onClick={() => removeImage.mutate(bookmark.id)}
                className="mt-1 text-xs text-rust hover:underline"
              >
                Remove current image
              </button>
            )}
          </div>
        )}

        <label className="flex items-center gap-2 pt-1 text-sm text-walnut">
          <input
            type="checkbox"
            checked={isQuickLink}
            onChange={(e) => setIsQuickLink(e.target.checked)}
            className="h-4 w-4 accent-terracotta"
          />
          Show in Quick links on the dashboard
        </label>

        {error && (
          <p role="alert" className="rounded-lg bg-rose/15 px-3 py-2 text-sm text-rust">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
